'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { type DayButton, DayPicker, getDefaultClassNames, type Locale } from 'react-day-picker';
import { mergeRefs } from '@repo/ui/lib/merge-refs';
import { cn } from '@repo/ui/lib/utils';
import { Button, buttonVariants } from '@repo/ui/components/ui/button';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

// Module scope: the React Compiler can't lower String.raw templates inside a component.
const RTL_NAV_ICON_CLASSES = [String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`, String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`];
function Calendar(t0) {
  const $ = _c(93);
  let className;
  let classNames;
  let components;
  let formatters;
  let locale;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      buttonVariant: t1,
      captionLayout: t2,
      className,
      classNames,
      components,
      formatters,
      locale,
      showOutsideDays: t3,
      weekStartsOn: t4,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = classNames;
    $[3] = components;
    $[4] = formatters;
    $[5] = locale;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    className = $[1];
    classNames = $[2];
    components = $[3];
    formatters = $[4];
    locale = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  const buttonVariant = t1 === undefined ? "ghost" : t1;
  const captionLayout = t2 === undefined ? "label" : t2;
  const showOutsideDays = t3 === undefined ? true : t3;
  const weekStartsOn = t4 === undefined ? 1 : t4;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[11] !== buttonVariant || $[12] !== captionLayout || $[13] !== className || $[14] !== props.showWeekNumber) {
    const defaultClassNames = getDefaultClassNames();
    T0 = DayPicker;
    t30 = captionLayout;
    if ($[43] !== className) {
      t31 = cn("group/calendar bg-background p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent", RTL_NAV_ICON_CLASSES, className);
      $[43] = className;
      $[44] = t31;
    } else {
      t31 = $[44];
    }
    t5 = cn(buttonVariants({
      variant: buttonVariant
    }), "size-(--cell-size) p-0 select-none aria-disabled:opacity-50", defaultClassNames.button_next);
    t6 = cn(buttonVariants({
      variant: buttonVariant
    }), "size-(--cell-size) p-0 select-none aria-disabled:opacity-50", defaultClassNames.button_previous);
    t7 = cn("font-medium select-none", captionLayout === "label" ? "text-sm" : "flex h-8 items-center gap-1 rounded-(--cell-radius) pr-1 pl-2 text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground", defaultClassNames.caption_label);
    t8 = cn("group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)", props.showWeekNumber ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)" : "[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)", defaultClassNames.day);
    t9 = cn("text-muted-foreground opacity-50", defaultClassNames.disabled);
    t10 = cn("absolute inset-0 bg-popover opacity-0", defaultClassNames.dropdown);
    t11 = cn("relative rounded-(--cell-radius)", defaultClassNames.dropdown_root);
    t12 = cn("flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium", defaultClassNames.dropdowns);
    t13 = cn("invisible", defaultClassNames.hidden);
    t14 = cn("flex w-full flex-col gap-4", defaultClassNames.month);
    t15 = cn("flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)", defaultClassNames.month_caption);
    t16 = "w-full border-collapse";
    t17 = cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months);
    t18 = cn("absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1", defaultClassNames.nav);
    t19 = cn("text-muted-foreground aria-selected:text-muted-foreground", defaultClassNames.outside);
    t20 = cn("relative isolate z-0 rounded-r-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-muted", defaultClassNames.range_end);
    t21 = cn("rounded-none", defaultClassNames.range_middle);
    t22 = cn("relative isolate z-0 rounded-l-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-muted", defaultClassNames.range_start);
    t23 = cn("w-full", defaultClassNames.root);
    t24 = cn("rounded-(--cell-radius) bg-muted text-foreground data-[selected=true]:rounded-none", defaultClassNames.today);
    t25 = cn("mt-2 flex w-full", defaultClassNames.week);
    t26 = cn("text-[0.8rem] text-muted-foreground select-none", defaultClassNames.week_number);
    t27 = cn("w-(--cell-size) select-none", defaultClassNames.week_number_header);
    t28 = cn("flex-1 rounded-(--cell-radius) text-[0.8rem] font-normal text-muted-foreground select-none", defaultClassNames.weekday);
    t29 = cn("flex", defaultClassNames.weekdays);
    $[11] = buttonVariant;
    $[12] = captionLayout;
    $[13] = className;
    $[14] = props.showWeekNumber;
    $[15] = T0;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
    $[24] = t18;
    $[25] = t19;
    $[26] = t20;
    $[27] = t21;
    $[28] = t22;
    $[29] = t23;
    $[30] = t24;
    $[31] = t25;
    $[32] = t26;
    $[33] = t27;
    $[34] = t28;
    $[35] = t29;
    $[36] = t30;
    $[37] = t31;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
  } else {
    T0 = $[15];
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    t14 = $[20];
    t15 = $[21];
    t16 = $[22];
    t17 = $[23];
    t18 = $[24];
    t19 = $[25];
    t20 = $[26];
    t21 = $[27];
    t22 = $[28];
    t23 = $[29];
    t24 = $[30];
    t25 = $[31];
    t26 = $[32];
    t27 = $[33];
    t28 = $[34];
    t29 = $[35];
    t30 = $[36];
    t31 = $[37];
    t5 = $[38];
    t6 = $[39];
    t7 = $[40];
    t8 = $[41];
    t9 = $[42];
  }
  let t32;
  if ($[45] !== classNames || $[46] !== t10 || $[47] !== t11 || $[48] !== t12 || $[49] !== t13 || $[50] !== t14 || $[51] !== t15 || $[52] !== t16 || $[53] !== t17 || $[54] !== t18 || $[55] !== t19 || $[56] !== t20 || $[57] !== t21 || $[58] !== t22 || $[59] !== t23 || $[60] !== t24 || $[61] !== t25 || $[62] !== t26 || $[63] !== t27 || $[64] !== t28 || $[65] !== t29 || $[66] !== t5 || $[67] !== t6 || $[68] !== t7 || $[69] !== t8 || $[70] !== t9) {
    t32 = {
      button_next: t5,
      button_previous: t6,
      caption_label: t7,
      day: t8,
      disabled: t9,
      dropdown: t10,
      dropdown_root: t11,
      dropdowns: t12,
      hidden: t13,
      month: t14,
      month_caption: t15,
      month_grid: t16,
      months: t17,
      nav: t18,
      outside: t19,
      range_end: t20,
      range_middle: t21,
      range_start: t22,
      root: t23,
      today: t24,
      week: t25,
      week_number: t26,
      week_number_header: t27,
      weekday: t28,
      weekdays: t29,
      ...classNames
    };
    $[45] = classNames;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
    $[59] = t23;
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
    $[63] = t27;
    $[64] = t28;
    $[65] = t29;
    $[66] = t5;
    $[67] = t6;
    $[68] = t7;
    $[69] = t8;
    $[70] = t9;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== locale) {
    t33 = t34 => {
      const {
        ...props_1
      } = t34;
      return <CalendarDayButton locale={locale} {...props_1} />;
    };
    $[72] = locale;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== components || $[75] !== t33) {
    t34 = {
      Chevron: _temp,
      DayButton: t33,
      Root: _temp2,
      WeekNumber: _temp3,
      ...components
    };
    $[74] = components;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== locale?.code) {
    t35 = date => date.toLocaleString(locale?.code, {
      month: "short"
    });
    $[77] = locale?.code;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== formatters || $[80] !== t35) {
    t36 = {
      formatMonthDropdown: t35,
      ...formatters
    };
    $[79] = formatters;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== T0 || $[83] !== locale || $[84] !== props || $[85] !== showOutsideDays || $[86] !== t30 || $[87] !== t31 || $[88] !== t32 || $[89] !== t34 || $[90] !== t36 || $[91] !== weekStartsOn) {
    t37 = <T0 captionLayout={t30} className={t31} classNames={t32} components={t34} formatters={t36} locale={locale} showOutsideDays={showOutsideDays} weekStartsOn={weekStartsOn} {...props} data-sentry-element="DayPicker" data-sentry-component="Calendar" data-sentry-source-file="calendar.tsx" />;
    $[82] = T0;
    $[83] = locale;
    $[84] = props;
    $[85] = showOutsideDays;
    $[86] = t30;
    $[87] = t31;
    $[88] = t32;
    $[89] = t34;
    $[90] = t36;
    $[91] = weekStartsOn;
    $[92] = t37;
  } else {
    t37 = $[92];
  }
  return t37;
}
function _temp3(t0) {
  const {
    children,
    ...props_3
  } = t0;
  return <td {...props_3}><div className="flex size-(--cell-size) items-center justify-center text-center">{children}</div></td>;
}
function _temp2(t0) {
  const {
    className: className_1,
    rootRef,
    ...props_2
  } = t0;
  return <div className={cn(className_1)} data-slot="calendar" ref={rootRef} {...props_2} />;
}
function _temp(t0) {
  const {
    className: className_0,
    orientation,
    ...props_0
  } = t0;
  if (orientation === "left") {
    return <ChevronLeftIcon className={cn("size-4", className_0)} {...props_0} />;
  }
  if (orientation === "right") {
    return <ChevronRightIcon className={cn("size-4", className_0)} {...props_0} />;
  }
  return <ChevronDownIcon className={cn("size-4", className_0)} {...props_0} />;
}
function CalendarDayButton(t0) {
  const $ = _c(25);
  let className;
  let day;
  let incomingRef;
  let locale;
  let modifiers;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      day,
      locale,
      modifiers,
      ref: incomingRef,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = day;
    $[3] = incomingRef;
    $[4] = locale;
    $[5] = modifiers;
    $[6] = props;
  } else {
    className = $[1];
    day = $[2];
    incomingRef = $[3];
    locale = $[4];
    modifiers = $[5];
    props = $[6];
  }
  const defaultClassNames = getDefaultClassNames();
  const ref = React.useRef(null);
  let t1;
  let t2;
  if ($[7] !== modifiers.focused) {
    t1 = () => {
      if (modifiers.focused) {
        ref.current?.focus();
      }
    };
    t2 = [modifiers.focused];
    $[7] = modifiers.focused;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  React.useEffect(t1, t2);
  const T0 = Button;
  const t3 = cn("relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 border-0 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50 data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-muted data-[range-middle=true]:text-foreground data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-foreground [&>span]:text-xs [&>span]:opacity-70", defaultClassNames.day, className);
  let t4;
  if ($[10] !== day.date || $[11] !== locale?.code) {
    t4 = day.date.toLocaleDateString(locale?.code);
    $[10] = day.date;
    $[11] = locale?.code;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = modifiers.range_end;
  const t6 = modifiers.range_middle;
  const t7 = modifiers.range_start;
  const t8 = modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;
  let t9;
  if ($[13] !== incomingRef) {
    t9 = mergeRefs(ref, incomingRef);
    $[13] = incomingRef;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== T0 || $[16] !== modifiers.range_end || $[17] !== modifiers.range_middle || $[18] !== modifiers.range_start || $[19] !== props || $[20] !== t3 || $[21] !== t4 || $[22] !== t8 || $[23] !== t9) {
    t10 = <T0 className={t3} data-day={t4} data-range-end={t5} data-range-middle={t6} data-range-start={t7} data-selected-single={t8} ref={t9} size="icon-lg" variant="ghost" {...props} data-sentry-element="Button" data-sentry-component="CalendarDayButton" data-sentry-source-file="calendar.tsx" />;
    $[15] = T0;
    $[16] = modifiers.range_end;
    $[17] = modifiers.range_middle;
    $[18] = modifiers.range_start;
    $[19] = props;
    $[20] = t3;
    $[21] = t4;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
export { Calendar, CalendarDayButton };
export type { DateRange } from 'react-day-picker';